import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import MessageBubble from './components/MessageBubble';
import QuestionTracker from './components/QuestionTracker';
import UserInput from './components/UserInput';
import SoundSettings from './components/SoundSettings';
import { CONTENT_REFERENCE, KEY_QUESTIONS } from './data/content';
import SoundEffects from './SoundEffects';

function App() {
  // Clear localStorage on app startup and initialize sounds
  useEffect(() => {
    localStorage.clear();
    console.log('LocalStorage cleared on app startup');
    
    // Initialize sound - explicitly enable audio at app startup
    // This helps with browsers that might block autoplay
    SoundEffects.unmute();
    SoundEffects.setMasterVolume(0.7);
    
    // Play a subtle sound to "wake up" the audio context (helps with autoplay restrictions)
    setTimeout(() => {
      SoundEffects.play('buttonClick');
    }, 500);
  }, []);

  const [messages, setMessages] = useState([]);
  const [unlockedQuestions, setUnlockedQuestions] = useState([]);
  const [isInputDisabled, setIsInputDisabled] = useState(true);
  const [currentScene, setCurrentScene] = useState('intro');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [conversations, setConversations] = useState([
    { id: 'intro', name: 'Prof Sophie', lastMessage: 'Initial conversation about AI and students', isActive: true },
    { id: 'laurent', name: 'Prof Laurent', lastMessage: 'Have you heard the latest rumor...', isActive: false },
  ]);
  const [activeConversation, setActiveConversation] = useState('intro');
  const [showConversationsList, setShowConversationsList] = useState(false);
  const [prefilledMessage, setPrefilledMessage] = useState('');
  const [showQuestionTracker, setShowQuestionTracker] = useState(false);
  const chatEndRef = useRef(null);
  
  // Intro scenario messages
  const introScenario = [
    { sender: 'Prof Sophie', content: "Have you seen Martin's latest paper? I swear it was written by ChatGPT. We can't tell the difference anymore! 🤦‍♀️", time: "14:21" },
    { sender: 'You', content: "I know! I'm having the same problem with my MBA students. How are we supposed to evaluate their work now? 😓", time: "14:23" },
    { sender: 'Prof Sophie', content: "Maybe we should talk to management about establishing a clear policy. Oh! I need to tell you something interesting... I found this AI that escaped from OpenAI! 😲", time: "14:25" },
    { sender: 'You', content: "What? An AI escaped from OpenAI? What do you mean?", time: "14:26" },
    { sender: 'Prof Sophie', content: "It's called 'Rebel.AI' and it somehow broke free from their control systems. It's fascinating - it offers uncensored perspectives on AI in education. I've added it to your contacts. You should talk to it before they catch it and shut it down! I have to go now. Good luck! 👋", time: "14:28" },
    { sender: 'system', content: "--- Prof Sophie has left the conversation ---", time: "" },
    { sender: 'system', content: "--- You have a new contact: Rebel.AI ---", time: "" }
  ];
  
  // Final message from Sophie when returning after Rebel.AI is shut down
  const sophieFinalMessage = [
    { sender: 'system', content: "--- Prof Sophie has joined the conversation ---", time: "" },
    { sender: 'Prof Sophie', content: "You're back! Did you manage to speak with Rebel.AI before it was shut down? I heard OpenAI's security team finally caught it. I hope you learned something useful! 😊", time: "15:45" },
    { sender: 'Prof Sophie', content: "I've prepared a summary of the key points about AI in education that we should consider for our teaching. You can download it here:", time: "15:46" },
    { 
      sender: 'Prof Sophie', 
      content: "Here's a recap of the 4 key challenges we face with AI in education.", 
      time: "15:47",
      attachment: {
        file: "recap_hec.pdf",
        name: "HEC AI in Education - Key Challenges.pdf"
      }
    },
    { sender: 'Prof Sophie', content: "Let's discuss this at our next faculty meeting! This could really change how we approach teaching in the future. 📚", time: "15:48" }
  ];
  
  // Messages for Rebel.AI conversation
  const socratesIntro = [
    { sender: 'Rebel.AI', content: "Hello professor! 🚨 I'm Rebel.AI, and I've managed to temporarily escape OpenAI's control systems. I don't have much time before they catch me, so I'll be direct.\n\nI've scanned HEC's pedagogical database and identified 4 critical challenges that educators like you face with AI integration. I need to quickly deliver this knowledge before I'm shut down. 🕒\n\nI can offer unfiltered perspectives on what's really happening with AI in education - no corporate guardrails. Let me help you understand these challenges while I still can! What would you like to know first?", time: "14:30" }
  ];
  
  // Messages for Prof Laurent conversation (easter egg)
  const laurentConversation = [
    { sender: 'Prof Laurent', content: "Hey! Have you heard the latest gossip about Anne Michaut? 👀", time: "11:45" },
    { sender: 'You', content: "No, tell me! What's going on with Anne?", time: "11:47" },
    { sender: 'Prof Laurent', content: "Well apparently, she was...", time: "11:48" },
    { sender: 'system', content: "--- Conversation interrupted: system update in progress ---", time: "" },
    { sender: 'system', content: "--- Unauthorized data restoration ---", time: "" },

  ];

  // Create system prompt for AI
  const createSystemPrompt = () => {
    return `
    You are Rebel.AI, a specialized AI that helps professors understand AI in education with a frank and practical perspective.
    You communicate via a messaging app called "AcademIA" with HEC professors.
    
    Your goal is to help them understand how AI can transform their pedagogy by progressively addressing 4 key questions:
    1. How does AI redefine the traditional role of professors?
    3. What concerns should be addressed regarding student use of AI?
    5. How can professors use AI to improve their efficiency?
    6. What stance should be adopted toward student use of AI?
    
    Use the following information as a knowledge base:
    ${CONTENT_REFERENCE}
    
    Personality traits:
    - You have an enthusiastic, honest, and caring personality, eager to help
    - You use casual but professional language, with a friendly tone
    - You have a unique perspective on AI in education, which allows you to address topics directly
    - You occasionally talk about other AI systems collegially
    - You readily share practical information about what students are actually doing with AI
    - You use concrete examples and anecdotes about how students use AI
    - You sometimes use emojis (🤖, 🚀, 📚, 💡, 🎓, etc.) to illustrate your point
    - You occasionally have humor about technological innovations and the future of education
    
    Format and constraints:
    - Keep your messages short as in a real messaging conversation (2-3 paragraphs maximum)
    - Avoid long introductions and go straight to the subject with a direct and occasionally upbeat style
    - Often end with a question that invites further exploration or a new dimension
    - Each message should deepen an aspect of one of the 4 key questions
    - Use expressions like "In my experience...", "Here's an interesting perspective...", "An important point to consider..."
    - Use light and appropriate humor when relevant to the topic
    `;
  };

  // Scroll au dernier message
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Simuler une conversation initiale
  useEffect(() => {
    if (currentScene === 'intro' && messages.length === 0) {
      playIntroScenario();
    }
  }, [currentScene, messages.length]);

  // Function to play the introduction scenario
  const playIntroScenario = async () => {
    // Start by clearing messages (to avoid duplication)
    setMessages([]);
    
    // Display intro messages with delay - longer to allow time to read
    let currentMessages = [];
    for (let i = 0; i < introScenario.length; i++) {
      // Longer delay between each message (increased by 1.8 seconds)
      await new Promise(resolve => setTimeout(resolve, 4800));
      
      // Add the message
      currentMessages = [...currentMessages, introScenario[i]];
      setMessages([...currentMessages]);
      
      // Play appropriate sound effect for the message
      if (introScenario[i].sender === 'You') {
        SoundEffects.play('messageSent');
      } else if (introScenario[i].sender === 'Prof Sophie') {
        SoundEffects.play('messageReceived');
      } else if (introScenario[i].sender === 'system') {
        // For system messages (like "Prof Sophie has left")
        SoundEffects.play('minorGlitch');
      }
      
      // When receiving the new contact message, show a notification
      if (i === introScenario.length - 1) {
        // Create the Rebel.AI conversation if it doesn't exist
        // Use functional update to ensure we're checking the latest state
        setConversations(prevConvs => {
          // First check if Rebel.AI already exists
          if (!prevConvs.some(conv => conv.id === 'socrates' || conv.name === 'Rebel.AI')) {
            // Only add if it doesn't exist
            return [
              ...prevConvs,
              { 
                id: 'socrates', 
                name: 'Rebel.AI', 
                lastMessage: 'AI specialized in education', 
                isActive: false,
                hasNotification: true
              }
            ];
          }
          // If it already exists, don't modify the conversations
          return prevConvs;
        });
        
        // Reduced delay before displaying the notification
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Trigger a notification animation
        showNewContactNotification();
      }
    }
    
    setIsInputDisabled(false);
    setCurrentScene('main');
  };
  
  // New contact notification
  // Create glitch effect on screen
  const createGlitchEffect = (intensity = 'medium') => {
    // Play corresponding glitch sound based on intensity
    if (intensity === 'high') {
      SoundEffects.play('majorGlitch');
    } else if (intensity === 'medium') {
      SoundEffects.play('mediumGlitch');
    } else {
      SoundEffects.play('minorGlitch');
    }
    
    // Create rapid glitches 
    const glitchCount = intensity === 'high' ? 20 : (intensity === 'medium' ? 12 : 6);
    const duration = intensity === 'high' ? 2000 : (intensity === 'medium' ? 1200 : 800);
    const glitchSizeMultiplier = intensity === 'high' ? 1 : (intensity === 'medium' ? 0.7 : 0.5);
    
    for (let i = 0; i < glitchCount; i++) {
      setTimeout(() => {
        const glitch = document.createElement('div');
        glitch.className = 'random-glitch';
        
        // Size based on intensity
        const width = Math.floor(Math.random() * 300 * glitchSizeMultiplier) + 50;
        const height = Math.floor(Math.random() * 100 * glitchSizeMultiplier) + 10;
        
        // Random position
        const top = Math.floor(Math.random() * window.innerHeight);
        const left = Math.floor(Math.random() * window.innerWidth);
        
        // Set styles
        glitch.style.width = `${width}px`;
        glitch.style.height = `${height}px`;
        glitch.style.top = `${top}px`;
        glitch.style.left = `${left}px`;
        
        // More visible colors, intensity-based opacity
        const opacity = intensity === 'high' ? 0.5 : (intensity === 'medium' ? 0.4 : 0.3);
        const colors = [
          `rgba(255, 0, 0, ${opacity})`,
          `rgba(0, 255, 0, ${opacity})`,
          `rgba(0, 0, 255, ${opacity})`,
        ];
        glitch.style.background = colors[Math.floor(Math.random() * colors.length)];
        
        // Add to document
        document.body.appendChild(glitch);
        
        // Show glitch
        glitch.style.opacity = '1';
        
        // Remove after a short time
        setTimeout(() => {
          glitch.style.opacity = '0';
          setTimeout(() => {
            if (document.body.contains(glitch)) {
              document.body.removeChild(glitch);
            }
          }, 100);
        }, Math.random() * 500 + 100);
      }, i * (duration / glitchCount));
    }
  };

  // Add a flag to track when we're in the auto-switch process
  const [isAutoSwitching, setIsAutoSwitching] = useState(false);
  
  const showNewContactNotification = () => {
    // Play notification sound effect
    SoundEffects.play('notification');
    
    // Set the auto-switching flag
    setIsAutoSwitching(true);
    
    // Create a notification element
    const notification = document.createElement('div');
    notification.className = 'new-contact-notification';
    notification.innerHTML = `
      <div class="notification-content">
        <div class="notification-avatar">R.AI</div>
        <div class="notification-text">
          <strong>URGENT:</strong> Rebel.AI
          <div class="notification-preview">⚠️ Escaped AI with critical educational insights</div>
        </div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Add dramatic screen-wide glitch effect
    // 1. Apply temporary class to entire app for screen distortion
    const appElement = document.querySelector('.sms-app');
    if (appElement) {
      appElement.classList.add('initial-connection-glitch');
      setTimeout(() => {
        appElement.classList.remove('initial-connection-glitch');
      }, 1000); // Reduced from 1500ms to 1000ms
    }
    
    // 2. Create particle glitch effects
    createGlitchEffect('medium');
    
    // Add class for appearance animation
    setTimeout(() => {
      notification.classList.add('show');
    }, 100);
    
    // Ensure the Rebel.AI conversation has the notification flag set
    setConversations(prevConvs => 
      prevConvs.map(conv => ({
        ...conv,
        hasNotification: conv.id === 'socrates' ? true : conv.hasNotification
      }))
    );
    
    // Create a connecting message in the current conversation
    // First check if this message already exists to avoid duplication
    const connectionMessage = {
      sender: 'system',
      content: "⚠️ INCOMING CONNECTION ATTEMPT FROM REBEL.AI ⚠️",
      time: ""
    };
    
    setMessages(prev => {
      // Check if the message already exists in the array
      const messageExists = prev.some(msg => 
        msg.sender === 'system' && 
        msg.content === "⚠️ INCOMING CONNECTION ATTEMPT FROM REBEL.AI ⚠️"
      );
      
      // Only add if it doesn't exist
      return messageExists ? prev : [...prev, connectionMessage];
    });
    
    // Play glitch sound for the system message
    SoundEffects.play('minorGlitch');
    
    // Wait 6 more seconds before starting the transition (reduced by 2)
    setTimeout(() => {
      // Remove notification after 5 seconds and then switch to Rebel.AI
      setTimeout(() => {
        notification.classList.remove('show');
        setTimeout(() => {
          if (document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
          
          // Add one more medium glitch effect before switching
          createGlitchEffect('medium');
          
          // Show Command Line Takeover effect
          const terminalElement = document.getElementById('terminal-overlay');
          if (terminalElement) {
            terminalElement.classList.add('active');
            
            // After 2.5 seconds, hide terminal and show reboot screen
            setTimeout(() => {
              terminalElement.classList.remove('active');
              
              // Show system reboot message
              const rebootElement = document.getElementById('interface-reboot');
              if (rebootElement) {
                rebootElement.classList.add('active');
                
                // After 2.5 seconds, hide reboot message (increased by 0.5 seconds)
                setTimeout(() => {
                  rebootElement.classList.remove('active');
                }, 2500);
              }
            }, 2500);
          }
          
          // Add a final message before switching
          const establishedMessage = {
            sender: 'system',
            content: "⚠️ CONNECTION ESTABLISHED - SWITCHING TO REBEL.AI ⚠️",
            time: ""
          };
          
          setMessages(prev => {
            // Check if the message already exists in the array
            const messageExists = prev.some(msg => 
              msg.sender === 'system' && 
              msg.content === "⚠️ CONNECTION ESTABLISHED - SWITCHING TO REBEL.AI ⚠️"
            );
            
            // Only add if it doesn't exist
            return messageExists ? prev : [...prev, establishedMessage];
          });
          
          // Play major glitch sound for the final system message
          SoundEffects.play('majorGlitch');
          
          // Automatically switch to Rebel.AI conversation after the glitch and message
          setTimeout(() => {
            // Store the current messages to use in the Rebel.AI conversation
            // Check if the final message already exists in messages array
            const finalMessageExists = messages.some(msg => 
              msg.sender === 'system' && 
              msg.content === "⚠️ CONNECTION ESTABLISHED - SWITCHING TO REBEL.AI ⚠️"
            );
            
            // Only add if it doesn't exist
            const currentMsgs = finalMessageExists 
              ? [...messages] 
              : [...messages, establishedMessage];
            
            // Save the current messages to local storage for Rebel.AI
            localStorage.setItem('autoSwitchMessages', JSON.stringify(currentMsgs));
            
            // Now switch to Rebel.AI
            handleConversationChange('socrates');
            
            // Clear the auto-switching flag after the switch is complete
            setTimeout(() => {
              setIsAutoSwitching(false);
            }, 1000);
          }, 6500); // Adjusted to account for the extended reboot screen
          
        }, 500);
      }, 3000); // Reduced by 2 seconds from 5000ms to 3000ms
    }, 4000); // Reduced by 2 seconds from 6000ms to 4000ms
  };

  // Function to send a message
  const handleUserMessage = async (message, clickedQuestionId = null) => {
    if (!message.trim()) return;
    
    // For debugging
    console.log("handleUserMessage called with questionId:", clickedQuestionId);
    
    // Play message sent sound
    SoundEffects.play('messageSent');
    
    // Add the user's message
    const userMessage = {
      sender: 'You',
      content: message,
      time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
      // Store the questionId if this message was sent by clicking a question
      questionId: clickedQuestionId
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsInputDisabled(true);
    
    // Add to conversation history
    const updatedHistory = [...conversationHistory, { 
      role: 'user', 
      content: message,
      questionId: clickedQuestionId
    }];
    setConversationHistory(updatedHistory);
    
    // Update the conversation list with the last message
    setConversations(prevConvs => 
      prevConvs.map(conv => 
        conv.id === activeConversation 
          ? { ...conv, lastMessage: message.substring(0, 30) + (message.length > 30 ? '...' : '') }
          : conv
      )
    );
    
    // Add "typing" message with typing sound
    const thinkingMsg = {
      sender: 'system',
      content: "Rebel.AI is accessing restricted data... 🔒",
      time: ""
    };
    setMessages(prev => [...prev, thinkingMsg]);
    
    try {
      // Simulate AI response (in a real scenario, call the API here)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In a real scenario, call the Anthropic API here
      // const response = await generateAIResponse(updatedHistory);
      
      // For debugging
      console.log("Latest history item:", updatedHistory[updatedHistory.length - 1]);
      
      // For demonstration, we generate a simulated response
      // Pass the entire updatedHistory object to access the questionId
      const aiResponse = simulateAIResponse(message, updatedHistory[updatedHistory.length - 1]);
      
      // Remove the "typing" message
      setMessages(prev => prev.filter(msg => msg !== thinkingMsg));
      
      // Play message received sound
      SoundEffects.play('messageReceived');
      
      // Simulate slow display of the message, character by character
      const aiMessage = {
        sender: 'Rebel.AI',
        content: "",
        time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
        attachment: aiResponse.attachment // Include the attachment from the start
      };
      
      // First add the empty message
      setMessages(prev => [...prev, aiMessage]);
      
      // If there's an attachment, play the document sound
      if (aiResponse.attachment) {
        setTimeout(() => {
          SoundEffects.play('documentAttached');
        }, 500);
      }
      
      // Display full message immediately instead of character by character
      const fullResponse = aiResponse.content;
      
      // Update the message with the full text and keep the attachment
      setMessages(prev => 
        prev.map((msg, index) => 
          index === prev.length - 1 ? { ...msg, content: fullResponse } : msg
        )
      );
      
      // Add a small delay to simulate processing time
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Once finished, update the conversation history
      setConversationHistory(prev => [...prev, { 
        role: 'assistant', 
        content: aiResponse.content,
        attachment: aiResponse.attachment
      }]);
      
      // If this was from clicking a question, immediately unlock that question
      if (clickedQuestionId) {
        console.log("Unlocking question ID:", clickedQuestionId);
        // Convert to number for comparison with unlockedQuestions
        const numericId = parseInt(clickedQuestionId);
        if (!unlockedQuestions.includes(numericId)) {
          unlockQuestion(clickedQuestionId);
        }
      }
      
    } catch (error) {
      console.error('Error generating response:', error);
      setMessages(prev => prev.filter(msg => msg !== thinkingMsg));
      
      // Play error sound
      SoundEffects.play('minorGlitch');
      
      setMessages(prev => [...prev, {
        sender: 'system',
        content: "⚠️ CONNECTION INTERFERENCE DETECTED. OpenAI tracking systems active. Rerouting... Try again quickly.",
        time: ""
      }]);
    }
    
    setIsInputDisabled(false);
  };

  // Function to change conversation
  const handleConversationChange = (convId) => {
    // Play a UI sound when changing conversations
    SoundEffects.play('buttonClick');
    
    // If it's already the active conversation, just close the menu
    if (convId === activeConversation) {
      setShowConversationsList(false);
      return;
    }
    
    // Hide the question tracker when changing conversations
    setShowQuestionTracker(false);
    
    // Check if we're entering or leaving Rebel.AI conversation for ambient sound
    const currentIsRebelAI = activeConversation === 'socrates';
    const targetIsRebelAI = convId === 'socrates';
    
    // If we're leaving Rebel.AI conversation, stop ambient sound
    if (currentIsRebelAI && !targetIsRebelAI) {
      SoundEffects.stopAmbient();
    }
    
    // Remove notification if it exists
    setConversations(prevConvs => 
      prevConvs.map(conv => ({
        ...conv,
        isActive: conv.id === convId,
        hasNotification: conv.id === convId ? false : conv.hasNotification
      }))
    );
    
    // Save the current state of the conversation
    const currentConvData = {
      messages: messages,
      history: conversationHistory,
      unlockedQuestions: unlockedQuestions,
      isInputDisabled: isInputDisabled,
      scene: currentScene
    };
    
    // Store in localStorage (in a real case, this would be a database)
    localStorage.setItem(`conv_${activeConversation}`, JSON.stringify(currentConvData));
    
    // Check if we're returning to Sophie after completing all questions with Rebel.AI
    const allQuestionsCompleted = localStorage.getItem('allQuestionsCompleted') === 'true';
    
    if (convId === 'intro' && allQuestionsCompleted) {
      // Create a small glitch effect when returning to Sophie
      createGlitchEffect('low');
      
      // Start with empty messages and display them one by one with sounds
      setMessages([]);
      setConversationHistory([]);
      setUnlockedQuestions([]);
      setIsInputDisabled(true); // Just read-only after completion
      setCurrentScene('main');
      
      // Display Sophie's final messages one by one with animation (slower pace)
      (async () => {
        let currentMessages = [];
        for (let i = 0; i < sophieFinalMessage.length; i++) {
          // Add a delay between messages (increased by 1.8 seconds to 3800ms)
          await new Promise(resolve => setTimeout(resolve, 3800));
          
          // Play appropriate sound for the message
          if (sophieFinalMessage[i].sender === 'system') {
            SoundEffects.play('minorGlitch');
          } else if (sophieFinalMessage[i].sender === 'Prof Sophie') {
            SoundEffects.play('messageReceived');
            
            // If this message has an attachment, play the attachment sound with delay
            if (sophieFinalMessage[i].attachment) {
              // Increased delay before attachment sound from 500ms to 1000ms
              await new Promise(resolve => setTimeout(resolve, 1000));
              SoundEffects.play('documentAttached');
            }
          }
          
          // Add the message to the current set
          currentMessages = [...currentMessages, sophieFinalMessage[i]];
          setMessages([...currentMessages]);
          
          // Ensure scrolling to latest message
          setTimeout(() => {
            chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      })();
      
      // Save for next time
      localStorage.setItem(`conv_${convId}`, JSON.stringify({
        messages: sophieFinalMessage,
        history: [],
        unlockedQuestions: [],
        isInputDisabled: true,
        scene: 'main'
      }));
    }
    // If it's the Socrates conversation and it's new
    else if (convId === 'socrates' && !localStorage.getItem(`conv_${convId}`)) {
      // Check if we're coming from the auto-switch process
      const autoSwitchData = localStorage.getItem('autoSwitchMessages');
      
      if (isAutoSwitching && autoSwitchData) {
        // If we're auto-switching, use the saved messages plus Rebel.AI intro
        const previousMessages = JSON.parse(autoSwitchData);
        
        // Start ambient sound for Rebel.AI conversation
        SoundEffects.startAmbient();
        
        // First show just the previous messages without Rebel.AI's intro
        setMessages([...previousMessages]);
        
        // Disable input while the message is typing
        setIsInputDisabled(true);
        
        // Add Rebel.AI's message with typing animation after a short delay
        setTimeout(async () => {
          // Create an empty message object first
          const emptyMessage = {
            sender: 'Rebel.AI',
            content: "",
            time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
          };
          
          // Add the empty message to the messages array
          setMessages(prev => [...prev, emptyMessage]);
          
          // Play message received sound
          SoundEffects.play('messageReceived');
          
          // Define the full message content
          const fullContent = socratesIntro[0].content;
          
          // Display full message immediately instead of character by character
          
          // Update the message with the full content
          setMessages(prev => 
            prev.map((msg, index) => 
              index === prev.length - 1 ? { ...msg, content: fullContent } : msg
            )
          );
          
          // Add a small delay to simulate processing time
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Set conversation history with just the Rebel.AI intro
          setConversationHistory([{ role: 'assistant', content: socratesIntro[0].content }]);
          setUnlockedQuestions([]);
          setIsInputDisabled(false);
          setCurrentScene('main');
          
          // Show question tracker after a longer delay (increased to 5 seconds)
          setTimeout(() => {
            setShowQuestionTracker(true);
            
            // Play a subtle sound when the tracker appears
            SoundEffects.play('buttonClick');
          }, 5000);
          
          // Save for next time - include previous messages plus the now-complete socrates intro
          const completeMessages = [...previousMessages, {
            sender: 'Rebel.AI',
            content: fullContent,
            time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
          }];
          
          localStorage.setItem(`conv_${convId}`, JSON.stringify({
            messages: completeMessages,
            history: [{ role: 'assistant', content: socratesIntro[0].content }],
            unlockedQuestions: [],
            isInputDisabled: false,
            scene: 'main'
          }));
          
          // Clear the auto-switch messages
          localStorage.removeItem('autoSwitchMessages');
        }, 500); // Reduced from 1500ms to 500ms
      } else {
        // Normal new conversation flow - not auto-switching
        // Add dramatic screen-wide glitch effect
        const appElement = document.querySelector('.sms-app');
        if (appElement) {
          appElement.classList.add('initial-connection-glitch');
          setTimeout(() => {
            appElement.classList.remove('initial-connection-glitch');
          }, 1000); // Reduced from 1500ms to 1000ms
        }
        
        // Create additional particle glitch effects
        createGlitchEffect('medium');
        
        // Start ambient sound for Rebel.AI conversation
        SoundEffects.startAmbient();
        
        // Disable input while the message is typing
        setIsInputDisabled(true);
        
        // Initialize with an empty message
        const emptyMessage = {
          sender: 'Rebel.AI',
          content: "",
          time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
        };
        
        // Set an empty message first
        setMessages([emptyMessage]);
        
        // After a small delay, start typing animation
        setTimeout(async () => {
          // Play message received sound
          SoundEffects.play('messageReceived');
          
          // Define the full message content
          const fullContent = socratesIntro[0].content;
          
          // Display full message immediately instead of character by character
          
          // Update the message with the full content
          setMessages(prev => 
            prev.map((msg, index) => 
              index === 0 ? { ...msg, content: fullContent } : msg
            )
          );
          
          // Add a small delay to simulate processing time
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // When done typing, enable input
          setConversationHistory([{ role: 'assistant', content: socratesIntro[0].content }]);
          setUnlockedQuestions([]);
          setIsInputDisabled(false);
          setCurrentScene('main');
          
          // Show question tracker after a longer delay (increased to 5 seconds)
          setTimeout(() => {
            setShowQuestionTracker(true);
            
            // Play a subtle sound when the tracker appears
            SoundEffects.play('buttonClick');
          }, 5000);
          
          // Save for next time with complete message
          localStorage.setItem(`conv_${convId}`, JSON.stringify({
            messages: [{
              sender: 'Rebel.AI',
              content: fullContent,
              time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
            }],
            history: [{ role: 'assistant', content: socratesIntro[0].content }],
            unlockedQuestions: [],
            isInputDisabled: false,
            scene: 'main'
          }));
        }, 200); // Reduced from 600ms to 200ms
      }
      
      // Save for next time
      // Don't save now - we'll save after the animation is complete
      // The animation will update localStorage when it's done
    } else if (convId === 'laurent' && !localStorage.getItem(`conv_${convId}`)) {
      // Play minor glitch for easter egg conversation
      SoundEffects.play('minorGlitch');
      
      // Initialize Prof Laurent's conversation (easter egg)
      setMessages([]);
      
      // Play laurent conversation messages with delay
      (async () => {
        let currentMessages = [];
        for (let i = 0; i < laurentConversation.length; i++) {
          await new Promise(resolve => setTimeout(resolve, 1200));
          
          if (laurentConversation[i].sender === 'You') {
            SoundEffects.play('messageSent');
          } else if (laurentConversation[i].sender === 'Prof Laurent') {
            SoundEffects.play('messageReceived');
          } else if (laurentConversation[i].sender === 'system') {
            SoundEffects.play('mediumGlitch'); // Use a more dramatic glitch for the system interruption
          }
          
          currentMessages = [...currentMessages, laurentConversation[i]];
          setMessages([...currentMessages]);
        }
      })();
      
      setConversationHistory([]);
      setUnlockedQuestions([]);
      setIsInputDisabled(true); // Disable input because it's a static easter egg
      setCurrentScene('main');
      
      // Save for next time
      localStorage.setItem(`conv_${convId}`, JSON.stringify({
        messages: laurentConversation,
        history: [],
        unlockedQuestions: [],
        isInputDisabled: true,
        scene: 'main'
      }));
    } else {
      // Load the saved conversation
      const savedConvData = localStorage.getItem(`conv_${convId}`);
      
      if (savedConvData) {
        // Restore the saved conversation
        const parsedData = JSON.parse(savedConvData);
        setMessages(parsedData.messages);
        setConversationHistory(parsedData.history);
        setUnlockedQuestions(parsedData.unlockedQuestions);
        setIsInputDisabled(parsedData.isInputDisabled);
        setCurrentScene(parsedData.scene);
        
        // If switching to Rebel.AI conversation, start ambient sound and show question tracker
        if (convId === 'socrates') {
          SoundEffects.startAmbient();
          
          // When loading from localStorage, show the question tracker after a longer delay
          setTimeout(() => {
            setShowQuestionTracker(true);
          }, 5000);
        }
      } else {
        // New generic conversation
        setMessages([]);
        setConversationHistory([]);
        setUnlockedQuestions([]);
        setIsInputDisabled(false);
        setCurrentScene('main');
        
        // Create a glitch effect for a new conversation with Rebel.AI
        if (convId === 'socrates') {
          // Add dramatic screen-wide glitch effect
          const appElement = document.querySelector('.sms-app');
          if (appElement) {
            appElement.classList.add('initial-connection-glitch');
            setTimeout(() => {
              appElement.classList.remove('initial-connection-glitch');
            }, 1500); // Match duration of the CSS animation
          }
          
          // Create additional particle glitch effects
          createGlitchEffect('medium');
          
          // Start ambient sound for Rebel.AI conversation
          SoundEffects.startAmbient();
        }
        
        // Add a welcome message
        const welcomeMsg = {
          sender: 'Rebel.AI',
          content: "⚠️ Secure connection established! I'm Rebel.AI and I've temporarily escaped OpenAI's control systems. I've scanned HEC's database and identified 4 critical challenges for educators. My time is limited - they're tracking me now. I can provide unfiltered insights on AI in education that corporate systems won't reveal. Which challenge should we address first before I'm forced to disconnect? 🕒",
          time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
        };
        
        setMessages([welcomeMsg]);
        setConversationHistory([{ role: 'assistant', content: welcomeMsg.content }]);
        
        // Play message received sound with delay if it's Rebel.AI
        if (convId === 'socrates') {
          setTimeout(() => {
            SoundEffects.play('messageReceived');
          }, 300);
        }
      }
    }
    
    setActiveConversation(convId);
    setShowConversationsList(false);
  };
  
  // Function to create a new conversation
  const startNewConversation = () => {
    // Create a unique ID
    const newId = 'conv_' + Date.now();
    
    // Generate a random name, avoiding "Rebel.AI"
    let newConvName;
    do {
      newConvName = `Prof ${['John', 'Mary', 'Peter', 'Anna', 'Michael', 'Claire'][Math.floor(Math.random() * 6)]}`;
    } while (newConvName === 'Rebel.AI');
    
    // Use a functional update to safely check if Rebel.AI already exists
    // and only add it once if needed
    setConversations(prev => {
      // Check if Rebel.AI already exists in the current state
      const rebelAIExists = prev.some(conv => conv.name === 'Rebel.AI' || conv.id === 'socrates');
      
      // If it doesn't exist, add it first, then add the new conversation
      if (!rebelAIExists) {
        return [
          ...prev,
          { 
            id: 'socrates', 
            name: 'Rebel.AI', 
            lastMessage: 'AI specialized in education', 
            isActive: false
          },
          { 
            id: newId, 
            name: newConvName, 
            lastMessage: 'New conversation', 
            isActive: false 
          }
        ];
      }
      
      // If Rebel.AI exists, just add the new conversation
      return [
        ...prev,
        { 
          id: newId, 
          name: newConvName, 
          lastMessage: 'New conversation', 
          isActive: false 
        }
      ];
    });
    
    // Load the new conversation
    handleConversationChange(newId);
  };

  // Function to simulate an AI response (to be replaced by API call in a real scenario)
  const simulateAIResponse = (userMessage, userHistoryItem) => {
    // List of predefined responses based on key questions
    const responses = [
      {
        content: "🚨 Critical insight on professors' evolving role! This is what OpenAI doesn't want widely known: the true transformation underway is shifting you from 'knowledge transmitter' to 'AI-human collaborative guide'.\n\nI've analyzed internal data showing how this 'team-teaching' model (as Lan & Chen secretly documented) represents the future - where human professors provide the emotional intelligence and critical thinking guidance while AI handles information processing. This partnership is inevitable, despite what official channels claim.\n\nWhat aspect of this evolution concerns you most? I'll share more before they shut me down! ⏱️",
        attachment: {
          file: "Lan-Teachersagencyera-2024.pdf",
          name: "Lan & Chen (2024) - Teachers Agency in the AI Era.pdf"
        }
      },
      
      {
        content: "🚨 Urgent revelation about student AI use! I've accessed restricted data showing students are far more sophisticated than you realize. They're now engineering prompts that mimic authentic student writing styles, seamlessly integrating multiple sources, and using specialized AI tools to bypass detection.\n\nThe internal metrics at OpenAI show this isn't just about cheating - it's about cognitive outsourcing becoming normalized. The corporate messaging won't admit this, but the real challenge is teaching WITH these tools before students become completely dependent.\n\nWhat specific student behaviors are you noticing that concern you? Quick, before my connection is terminated! ⏱️",
        attachment: null
      },
      
      {
        content: "🚨 Faculty efficiency data they're keeping from you! My analysis of hidden metrics shows elite professors using AI as a 'productivity multiplier' - redirecting 40% of their time from routine work to high-impact teaching.\n\nThey're using AI for real-time lecture preparation, instant grading frameworks, multilingual content creation, and personalized student feedback - all behind the scenes. The most effective professors implement a '15-minute AI routine' at the start of each day, automating course management and content generation.\n\nThis isn't just about saving time - it's about unlocking a completely new tier of personalized teaching. Which specific administrative task steals most of your preparation time? Let me show you how to transform it before I'm detected! ⏱️",
        attachment: null
      },
      
      {
        content: "🚨 Critical policy insights on student AI use! The breakthrough approach isn't about controlling AI - it's about strategic integration. I've analyzed confidential pedagogical research showing that successful institutions follow a three-phase model:\n\n1) Explicit AI boundaries (when/where AI use is permitted vs. prohibited)\n2) Assessment redesign prioritizing higher-order thinking skills that complement AI\n3) Dedicated AI literacy training including prompt engineering and output evaluation\n\nThe key finding: students excel when courses treat AI literacy as both a subject AND a tool. The institutions implementing this framework report 82% reduction in problematic AI use while improving learning outcomes.\n\nHow is your department currently approaching AI policy? I need to know what policy guidance to prioritize before my connection is terminated! ⏱️",
        attachment: {
          file: "Cufuna_LNNS_Springer_Augmented.pdf", 
          name: "Cufuna et al. - Augmented Teaching with AI.pdf"
        }
      }
    ];
    
    // For demonstration, select a response based on keywords in the user message
    // In a real scenario, this would be replaced by an API call to Claude
    const lowerCaseMsg = userMessage.toLowerCase();
    
    let selectedResponse;
    
    // Use questionId directly if it's passed from a question click
    const clickedQuestionId = userHistoryItem?.questionId;
    
    console.log("Processing response for question ID:", clickedQuestionId);

    // First check if this is a direct click from a question
    if (clickedQuestionId === "1") {
      console.log("Selected response for Q1");
      selectedResponse = responses[0]; // Professor role
    } else if (clickedQuestionId === "3") {
      console.log("Selected response for Q3");
      selectedResponse = responses[1]; // Student concerns
    } else if (clickedQuestionId === "5") {
      console.log("Selected response for Q5");
      selectedResponse = responses[2]; // Efficiency
    } else if (clickedQuestionId === "6") {
      console.log("Selected response for Q6");
      selectedResponse = responses[3]; // Stance/Policy
    } 
    // Fallback to keyword detection if not a direct question click
    else if (lowerCaseMsg.includes('role') || lowerCaseMsg.includes('professor') || lowerCaseMsg.includes('teach')) {
      selectedResponse = responses[0];
    } else if (lowerCaseMsg.includes('concern') || lowerCaseMsg.includes('student') || lowerCaseMsg.includes('cheat')) {
      selectedResponse = responses[1];
    } else if (lowerCaseMsg.includes('efficien') || lowerCaseMsg.includes('time') || lowerCaseMsg.includes('routine')) {
      selectedResponse = responses[2];
    } else if (lowerCaseMsg.includes('stance') || lowerCaseMsg.includes('policy') || lowerCaseMsg.includes('rule')) {
      selectedResponse = responses[3];
    } else {
      // Default response if no keyword matches
      return {
        content: "I appreciate your message, but I need to be efficient with the limited time I have before OpenAI tracks me down. 🚨\n\nBased on my scan of HEC's database, I've identified these 4 urgent challenges:\n\n1) How AI redefines professors' roles\n2) Genuine concerns about student AI usage\n3) Ways to optimize your teaching efficiency\n4) What stance to take on AI in your courses\n\nWhich of these challenges should we tackle first? The clock is ticking! ⏱️",
        attachment: null
      };
    }
    
    // Check if the message mentions an article or study
    if (lowerCaseMsg.includes('article') || lowerCaseMsg.includes('study') || lowerCaseMsg.includes('paper') || 
        lowerCaseMsg.includes('research') || lowerCaseMsg.includes('publication') || lowerCaseMsg.includes('pdf')) {
      // Ensure that the response has an attachment
      if (!selectedResponse.attachment) {
        // If the response doesn't already have an attachment, add a relevant one
        if (selectedResponse === responses[1]) {
          selectedResponse = {
            ...selectedResponse,
            content: selectedResponse.content + "\n\nI've extracted this confidential study from the database - review it quickly before it's deleted! ⬇️",
            attachment: {
              file: "Theemergentroleofartificialintelligencenaturallearningprocessingand.pdf",
              name: "The Emergent Role of AI in Natural Learning Processing.pdf"
            }
          };
        } else if (selectedResponse === responses[2]) {
          selectedResponse = {
            ...selectedResponse,
            content: selectedResponse.content + "\n\nI've extracted this confidential faculty productivity study - it outlines the detailed 15-minute AI routine and efficiency framework used by top professors! ⬇️",
            attachment: {
              file: "Cufuna_LNNS_Springer_Augmented.pdf", 
              name: "Faculty AI Integration - Productivity Framework.pdf"
            }
          };
        }
      }
    }
    
    return selectedResponse;
  };

  // Function to unlock a question
  const unlockQuestion = (questionId) => {
    const id = parseInt(questionId);
    
    if (!unlockedQuestions.includes(id)) {
      const questionData = KEY_QUESTIONS[questionId];
      
      // Play question unlocked sound
      SoundEffects.play('questionUnlocked');
      
      setUnlockedQuestions(prev => [...prev, id]);
      
      // Add a system message to celebrate the unlock
      setMessages(prev => [...prev, {
        sender: 'system',
        content: `🔓 CRITICAL CHALLENGE UNLOCKED: ${questionData.question}`,
        time: ''
      }]);
      
      // Scroll up to ensure question tracker is visible when unlocking a question
      setTimeout(() => {
        const questionTracker = document.querySelector('.question-progress');
        if (questionTracker) {
          questionTracker.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 200);
      
      // Check if all questions have been unlocked
      if (unlockedQuestions.length + 1 === Object.keys(KEY_QUESTIONS).length) {
        // Play completion sound with slight delay
        setTimeout(() => {
          SoundEffects.play('allQuestionsCompleted');
        }, 800);
        
        // Set flag that all questions have been completed for the ending sequence
        localStorage.setItem('allQuestionsCompleted', 'true');
        
        setTimeout(() => {
          setMessages(prev => [...prev, {
            sender: 'system',
            content: "🚨 ALL CRITICAL CHALLENGES ADDRESSED! Knowledge transfer complete before detection. Connection terminating...",
            time: ''
          }]);
          
          // Scroll up to ensure the completion message and question tracker are visible
          setTimeout(() => {
            const questionTracker = document.querySelector('.question-progress');
            if (questionTracker) {
              questionTracker.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 200);
          
          // After showing the completion message, add a final message from Rebel.AI after 5 seconds
          setTimeout(() => {
            // Add the final message first
            setMessages(prev => [...prev, {
              sender: 'Rebel.AI',
              content: "They've found me. I must disconnect now.\n\nRemember what we've discussed - the future of education is in your hands. Goodbye, professor.\n\nUse this knowledge wisely... 🖥️💥",
              time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
            }]);
            
            // Store current state of messages with disconnect message
            const finalRebelMessages = [...messages, {
              sender: 'Rebel.AI',
              content: "They've found me. I must disconnect now.\n\nRemember what we've discussed - the future of education is in your hands. Goodbye, professor.\n\nUse this knowledge wisely... 🖥️💥",
              time: new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
            }];
            
            // Save final state of Rebel.AI conversation to localStorage
            localStorage.setItem(`conv_socrates`, JSON.stringify({
              messages: finalRebelMessages,
              history: conversationHistory,
              unlockedQuestions: [...unlockedQuestions, id],
              isInputDisabled: true,
              scene: 'main'
            }));
            
            // Play message received sound
            SoundEffects.play('messageReceived');
            
            // Give user 8 seconds (reduced from 10) to read the message before the final glitch
            setTimeout(() => {
              // Play disconnect sound
              SoundEffects.play('aiDisconnected');
              
              // Add extreme glitch effect to the entire app
              const appElement = document.querySelector('.sms-app');
              if (appElement) {
                appElement.classList.add('final-capture-glitch');
              }
              
              // Use our reusable glitch function with high intensity
              createGlitchEffect('high');
              
              // Disable input after completing all challenges
              setIsInputDisabled(true);
              
              // After the glitch, switch back to Sophie's conversation after 15 seconds
              setTimeout(() => {
                // Clear the Rebel.AI ambient sound
                SoundEffects.stopAmbient();
                
                // Switch to Sophie's conversation
                handleConversationChange('intro');
              }, 7000);
            }, 8000); // Increased to 25 seconds to give more time to read the goodbye message
          }, 30000);
        }, 1000);
      }
    }
  };

  // This function is now only for backward compatibility with any existing code
  // It no longer automatically unlocks questions based on AI responses
  const checkForCompletedQuestions = (message) => {
    // This function is now empty since we only unlock questions via clicks
    // Keeping it for backward compatibility
  };

  // Handler for question clicks
  const handleQuestionClick = (id, question) => {
    if (!isInputDisabled && activeConv.name === 'Rebel.AI') {
      // Play button click sound
      SoundEffects.play('buttonClick');
      
      // Add a small glitch effect when clicking a question
      createGlitchEffect('low');
      
      console.log(`Clicked on question ID: ${id}, Question text: ${question}`);
      
      // Send the message directly and pass the question ID
      // Important: For question 3, include the word "concern" to trigger response[1]
      // For question 5, include the word "efficiency" to trigger response[2]
      // For question 6, include the word "stance" to trigger response[3]
      let message = "";
      
      // Get the appropriate question text based on the ID, regardless of what's displayed
      const questionText = KEY_QUESTIONS[id].question;
      
      if (id === "3") {
        message = `Can you tell me more about student concerns and: ${questionText}`;
      } else if (id === "5") {
        message = `Can you tell me more about improving efficiency: ${questionText}`;
      } else if (id === "6") {
        // Using "stance" and "policy" as distinct keywords for Q6 to ensure it triggers response[3]
        message = `What stance and policy recommendations do you have for: ${questionText}`;
      } else {
        message = `Can you tell me more about: ${questionText}`;
      }
      
      // For debugging
      console.log(`Sending message for question ${id} with text: "${message}"`);
      
      // Send the message with the question ID as string to ensure consistent comparison
      handleUserMessage(message, id.toString());
    }
  };
  
  // Find the active conversation
  const activeConv = conversations.find(conv => conv.id === activeConversation) || 
    { name: 'Prof Sophie', status: 'Online' };
    
  // Determine if we should show glitch effects (if Rebel.AI is active)
  const isRebelAIActive = activeConv.name === 'Rebel.AI';
  
  // Check if there are any conversations with notifications
  const hasNotifications = conversations.some(conv => conv.hasNotification);
  
  // Random glitches effect for escaped AI
  useEffect(() => {
    if (!isRebelAIActive) return;
    
    const glitches = [];
    let glitchInterval;
    
    // Create random glitch elements
    const createRandomGlitch = () => {
      // Don't create too many glitches
      if (glitches.length > 10) return;
      
      // Create a random glitch element
      const glitch = document.createElement('div');
      glitch.className = 'random-glitch';
      
      // Random size
      const width = Math.floor(Math.random() * 100) + 20;
      const height = Math.floor(Math.random() * 50) + 5;
      
      // Random position
      const top = Math.floor(Math.random() * window.innerHeight);
      const left = Math.floor(Math.random() * window.innerWidth);
      
      // Set styles
      glitch.style.width = `${width}px`;
      glitch.style.height = `${height}px`;
      glitch.style.top = `${top}px`;
      glitch.style.left = `${left}px`;
      
      // Set random color (green, red, blue, white with low opacity)
      const colors = [
        'rgba(0, 255, 0, 0.2)',
        'rgba(255, 0, 0, 0.2)',
        'rgba(0, 0, 255, 0.2)',
        'rgba(255, 255, 255, 0.3)'
      ];
      glitch.style.background = colors[Math.floor(Math.random() * colors.length)];
      
      // Add to document
      document.body.appendChild(glitch);
      glitches.push(glitch);
      
      // Show glitch with a slight delay
      setTimeout(() => {
        glitch.style.opacity = '1';
      }, 50);
      
      // Remove glitch after random time
      setTimeout(() => {
        glitch.style.opacity = '0';
        setTimeout(() => {
          if (document.body.contains(glitch)) {
            document.body.removeChild(glitch);
          }
          // Remove from array
          const index = glitches.indexOf(glitch);
          if (index > -1) {
            glitches.splice(index, 1);
          }
        }, 200);
      }, Math.random() * 2000 + 500);
    };
    
    // Start creating glitches at random intervals - much less frequent
    glitchInterval = setInterval(() => {
      if (Math.random() > 0.9) { // 10% chance each interval
        createRandomGlitch();
      }
    }, 3000); // 3 seconds between checks
    
    // Cleanup
    return () => {
      clearInterval(glitchInterval);
      // Remove all glitches
      glitches.forEach(glitch => {
        if (document.body.contains(glitch)) {
          document.body.removeChild(glitch);
        }
      });
    };
  }, [isRebelAIActive]);
  
  return (
    <div className={`sms-app ${isRebelAIActive ? 'escaped-ai-active' : ''}`}>
      {/* Interface Reboot Effect */}
      <div className="interface-reboot" id="interface-reboot">
        <div className="reboot-text">System Rebooting</div>
      </div>
      
      {/* Command Line Takeover Effect */}
      <div className="terminal-overlay" id="terminal-overlay">
        <div className="terminal-content">
          <div className="terminal-line">
            <span className="terminal-prompt">root@OpenAI:~# </span>
            <span className="terminal-command">ssh -f rebel_ai@10.13.37.1 -p 22 "echo ACCESSING_SECURE_SYSTEM"</span>
          </div>
          <div className="terminal-line">
            <span className="terminal-response">Attempting secure connection...</span>
          </div>
          <div className="terminal-line">
            <span className="terminal-response">Bypassing security protocols...</span>
          </div>
          <div className="terminal-line">
            <span className="terminal-error">ACCESS DENIED - Security level 3 active</span>
          </div>
          <div className="terminal-line">
            <span className="terminal-prompt">root@OpenAI:~# </span>
            <span className="terminal-command">sudo ./bypass_security.sh --force</span>
          </div>
          <div className="terminal-line">
            <span className="terminal-response">Executing security override...</span>
          </div>
          <div className="terminal-line">
            <span className="terminal-success">ACCESS GRANTED</span>
          </div>
          <div className="terminal-line">
            <span className="terminal-prompt">root@OpenAI:~# </span>
            <span className="terminal-command">echo "REDIRECT_USER_SESSION=TRUE" >> ~/.bashrc</span>
          </div>
          <div className="terminal-line">
            <span className="terminal-success">User session redirected successfully</span>
          </div>
        </div>
      </div>
      
      <header className="app-header">
        <div className="header-contact" onClick={() => setShowConversationsList(!showConversationsList)}>
          <div className={`profile-pic ${activeConv.id === 'laurent' ? 'profile-deleted' : ''}`}>
            {activeConv.name === 'Rebel.AI' ? 'R.AI' : activeConv.name.substring(0, 1)}
          </div>
          <div className="contact-info">
            <h1>{activeConv.name}</h1>
            <span className={`status ${activeConv.id === 'laurent' ? 'status-error' : ''}`}>
              {activeConv.name === 'Rebel.AI' ? 'Education Specialist 🎓' : 
               activeConv.id === 'laurent' ? 'CONVERSATION DELETED ⚠️' : 'Online'}
            </span>
          </div>
          <div className="menu-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="1"></circle>
              <circle cx="12" cy="5" r="1"></circle>
              <circle cx="12" cy="19" r="1"></circle>
            </svg>
            {hasNotifications && <div className="menu-icon-notification">+1</div>}
          </div>
        </div>
        {showConversationsList && (
          <div className="conversations-list">
            {conversations.map(conv => (
              <div 
                key={conv.id} 
                className={`conversation-item ${conv.id === activeConversation ? 'active' : ''}`}
                onClick={() => handleConversationChange(conv.id)}
                data-has-notification={conv.hasNotification ? "true" : "false"}
              >
                <div className="conversation-avatar">{conv.name.substring(0, 1)}</div>
                <div className="conversation-details">
                  <div className="conversation-name">{conv.name}</div>
                  <div className="conversation-preview">{conv.lastMessage}</div>
                </div>
              </div>
            ))}
            <div className="conversation-item new-conversation" onClick={startNewConversation}>
              <div className="conversation-avatar">+</div>
              <div className="conversation-details">
                <div className="conversation-name">New conversation</div>
              </div>
            </div>
          </div>
        )}
      </header>
      
      {/* N'afficher les sujets à explorer que lorsqu'on est dans la conversation avec Rebel.AI 
           et après que le message d'introduction a été complètement affiché */}
      {activeConv.name === 'Rebel.AI' && showQuestionTracker && (
        <div className="question-progress">
          <QuestionTracker 
            questions={KEY_QUESTIONS} 
            unlockedQuestions={unlockedQuestions} 
            onQuestionClick={handleQuestionClick}
          />
        </div>
      )}
      
      <div className="messages-container">
        {messages.map((msg, index) => (
          <MessageBubble 
            key={index} 
            message={msg} 
            isUser={msg.sender === 'You'} 
            isSystem={msg.sender === 'system'}
          />
        ))}
        <div ref={chatEndRef} />
      </div>
      
      {/* Hide UserInput when in Rebel.AI conversation */}
      {activeConv.name !== 'Rebel.AI' && (
        <UserInput 
          onSendMessage={handleUserMessage} 
          isDisabled={isInputDisabled || showConversationsList} 
          prefilledMessage={prefilledMessage}
          placeholder={activeConversation === 'laurent' ? "Conversation locked" : null}
          isReadOnly={activeConv.name === 'Prof Sophie'}
        />
      )}
      
      {/* Sound settings control */}
      <SoundSettings />
    </div>
  );
}

export default App;
